<template>
  <div class="checklist-container">
    <div v-if="!localChecklist && canUpdate && showCreateButton" class="checklist-empty">
      <div class="checklist-empty-actions">
        <button class="btn-sm btn-primary" @click="showCreateForm = true">
          {{ s__('Checklist|Create Checklist') }}
        </button>
      </div>
    </div>

    <div v-else-if="localChecklist" class="checklist">
      <!-- Checklist Header -->
      <div class="checklist-header">
        <div class="checklist-title-section">
          <div class="checklist-title-display">
            <div class="checklist-title-row">
              <div class="checklist-progress">
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: `${completionPercentage}%` }"></div>
                </div>
                <span class="progress-text">
                  {{ completedItemsCount }}/{{ checklistItems.length }}
                  {{ s__('Checklist|completed') }}
                </span>
              </div>
              <div class="checklist-actions">
                <button
                  class="btn-icon-small"
                  :title="
                    checklistCopyFeedbackVisible
                      ? s__('Checklist|Copied')
                      : s__('Checklist|Copy checklist')
                  "
                  @click="copyChecklistToClipboard"
                >
                  <CopyIcon size="16" color="#6b7280" />
                </button>
                <button
                  v-if="canUpdate"
                  class="btn-icon-small"
                  :title="s__('Checklist|Delete checklist')"
                  @click="confirmDelete"
                >
                  <TrashIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Checklist Items -->
      <div v-if="checklistItems.length > 0" class="checklist-items">
        <ul ref="itemsList" class="checklist-items-list" :class="{ 'is-dragging': isDragging }">
          <li
            v-for="item in sortedItems"
            :key="item.id"
            class="checklist-item"
            :class="{ completed: item.completed }"
            :data-item-id="item.id"
          >
            <button
              v-if="canUpdate && !editingItems[item.id] && !isMobileScreen"
              type="button"
              class="item-drag-handle"
              :disabled="saving"
              :title="s__('Checklist|Drag to reorder')"
              :aria-label="s__('Checklist|Drag to reorder')"
            >
              <span class="drag-handle-dots" aria-hidden="true"></span>
            </button>
            <div v-if="!editingItems[item.id]" class="item-content">
              <input
                :id="`item-${item.id}`"
                :checked="item.completed"
                type="checkbox"
                class="item-checkbox"
                :disabled="!canUpdate || saving"
                @change="toggleItem(item)"
              />
              <label :for="`item-${item.id}`" class="item-label">
                <span class="item-text">{{ item.title }}</span>
              </label>
            </div>
            <div v-if="!editingItems[item.id]" class="item-actions">
              <button
                class="btn-icon-small"
                :title="s__('Checklist|Copy item')"
                @click="copyChecklistItemToClipboard(item)"
              >
                <CopyIcon size="16" color="#6b7280" />
              </button>
              <button
                v-if="canUpdate"
                class="btn-icon-small"
                :title="s__('Checklist|Edit item')"
                @click="startEditItem(item)"
              >
                <EditIcon />
              </button>
              <button
                v-if="canUpdate"
                class="btn-icon-small"
                :title="s__('Checklist|Delete item')"
                @click="confirmDeleteItem(item)"
              >
                <TrashIcon />
              </button>
            </div>
            <div v-if="editingItems[item.id]" class="item-edit-form">
              <SingleLineTextarea
                ref="itemInput"
                v-model="editingItems[item.id].title"
                class="item-edit-input"
                @keydown.enter="saveItem(item)"
                @keydown.esc="cancelEditItem(item)"
              />
              <div class="item-edit-actions">
                <button class="btn-sm" @click="saveItem(item)">{{ s__('Checklist|Save') }}</button>
                <button class="btn-sm btn-secondary" @click="cancelEditItem(item)">
                  {{ s__('Checklist|Cancel') }}
                </button>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <!-- Empty State -->
      <div v-else class="checklist-empty-items">
        <p class="muted">{{ s__('Checklist|No items yet. Add items below.') }}</p>
      </div>

      <!-- Checklist Actions -->
      <div v-if="canUpdate" class="checklist-footer">
        <div class="checklist-footer-actions">
          <button
            v-if="checklistItems.length > 0"
            class="btn-sm btn-secondary"
            :disabled="saving || allCompleted"
            @click="bulkToggle(true)"
          >
            {{ s__('Checklist|Check All') }}
          </button>
          <button
            v-if="checklistItems.length > 0"
            class="btn-sm btn-secondary"
            :disabled="saving || allIncomplete"
            @click="bulkToggle(false)"
          >
            {{ s__('Checklist|Uncheck All') }}
          </button>
          <button v-if="!showAddItemForm" class="btn-sm" @click="showAddItemForm = true">
            {{ s__('Checklist|Add Checklist Item') }}
          </button>
        </div>

        <!-- Add Item Form -->
        <div v-if="showAddItemForm" class="add-item-form">
          <SingleLineTextarea
            ref="newItemInput"
            v-model="newItemForm.title"
            class="item-input"
            :placeholder="s__('Checklist|Enter item title')"
            @keydown.enter="handleAddItemEnter"
            @keydown.tab="handleAddItemTab"
            @keydown.esc="cancelAddItem"
          />
          <div class="add-item-actions">
            <button class="btn-sm" :disabled="!canAddItem || saving" @click="addItem">
              {{ s__('Checklist|Add') }}
            </button>
            <button class="btn-sm btn-secondary" @click="cancelAddItem">
              {{ s__('Checklist|Cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Checklist Form -->
    <div v-if="showCreateForm" class="create-checklist-form">
      <div class="form-row">
        <label>{{ s__('Checklist|First checklist item') }}</label>
        <SingleLineTextarea
          ref="createItemInput"
          v-model="createForm.itemTitle"
          :placeholder="s__('Checklist|Enter item title')"
          @keydown.enter="createChecklist"
          @keydown.esc="cancelCreate"
        />
      </div>
      <div class="form-actions">
        <button class="btn-sm" :disabled="!canCreateChecklist || creating" @click="createChecklist">
          {{ creating ? s__('Checklist|Creating...') : s__('Checklist|Create') }}
        </button>
        <button class="btn-sm btn-secondary" @click="cancelCreate">
          {{ s__('Checklist|Cancel') }}
        </button>
      </div>
      <div v-if="error" class="error">{{ error }}</div>
    </div>

    <!-- Warning when checklist is incomplete and blocking transition -->
    <div
      v-if="localChecklist && !checklistCompleted && blockingTransition"
      class="checklist-warning"
    >
      ⚠️ {{ s__('Checklist|Checklist must be completed before transitioning to this status') }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __ } from '@/locale'
import Sortable from 'sortablejs'
import EditIcon from '../../common/icons/EditIcon.vue'
import TrashIcon from '../../common/icons/TrashIcon.vue'
import CopyIcon from '../../common/icons/CopyIcon.vue'
import SingleLineTextarea from '../../common/SingleLineTextarea.vue'

const props = defineProps({
  taskId: { type: [String, Number], required: true },
  checklist: { type: Object, default: null },
  checklistCompleted: { type: Boolean, default: true },
  canUpdate: { type: Boolean, default: false },
  blockingTransition: { type: Boolean, default: false },
  showCreateButton: { type: Boolean, default: true },
})

const emit = defineEmits(['checklist-created', 'checklist-updated', 'checklist-deleted'])

// Local reactive state that syncs with props but can be updated immediately
const localChecklist = ref(null)
// Track if we're in the middle of a mutation to prevent props from overwriting our changes
const mutationInProgress = ref(false)

function normalizeChecklist(checklist) {
  if (!checklist) return null

  return {
    ...checklist,
    items: Array.isArray(checklist.items) ? checklist.items : [],
  }
}

// Helper to check if props checklist has equal or better data than local state
// Returns true if we should sync from props, false if local state is better/newer
function shouldSyncFromProps(propsChecklist) {
  // Never sync if we're in the middle of a mutation
  if (mutationInProgress.value) {
    return false
  }

  // If no local state, always sync (initial load)
  if (!localChecklist.value) {
    return true
  }

  // If no props, don't sync (keep local state) - parent is probably refetching
  if (!propsChecklist) {
    return false
  }

  // If different checklist IDs, sync if props has a checklist
  if (propsChecklist.id !== localChecklist.value.id) {
    // Only sync if local state is null or if props has a different checklist
    // This handles the case where we create a new checklist and props updates
    if (!localChecklist.value) {
      return true
    }
    // If we have local state with a different ID, don't sync (we're working on a different checklist)
    return false
  }

  // Same checklist - compare item counts
  const propsItemCount = propsChecklist.items?.length || 0
  const localItemCount = localChecklist.value.items?.length || 0

  // If props has more items, it's newer - sync
  if (propsItemCount > localItemCount) {
    return true
  }

  // If props has fewer items, local is newer - don't sync
  if (propsItemCount < localItemCount) {
    return false
  }

  // Same item count - check if props has items we don't have
  if (propsItemCount > 0) {
    const propsItemIds = new Set((propsChecklist.items || []).map(i => i.id))
    const localItemIds = new Set((localChecklist.value.items || []).map(i => i.id))

    // If props has items we don't have, sync
    for (const id of propsItemIds) {
      if (!localItemIds.has(id)) {
        return true
      }
    }

    // If we have items props doesn't have, don't sync (local is newer)
    for (const id of localItemIds) {
      if (!propsItemIds.has(id)) {
        return false
      }
    }

    // Same items - compare item states to see if props has updates
    // Check if any item in props has different state than local
    const propsItemsMap = new Map((propsChecklist.items || []).map(i => [i.id, i]))
    const localItemsMap = new Map((localChecklist.value.items || []).map(i => [i.id, i]))

    // If any item state differs, prefer local state (user's recent action)
    for (const [id, localItem] of localItemsMap) {
      const propsItem = propsItemsMap.get(id)
      if (propsItem) {
        // If states differ, prefer local (user's recent action)
        if (propsItem.completed !== localItem.completed || propsItem.title !== localItem.title) {
          return false
        }
      }
    }

    // Same items with same states - props might be from a refetch, but local is what user sees
    // Prefer local state to avoid flickering
    return false
  }

  // No items in either - props might be from initial load, prefer local if it exists
  return false
}

// Track if this is the first time we're setting local state (initial mount)
const isInitialMount = ref(true)

// Watch props to sync local state when parent updates
// Only sync if props has equal or better data than local state
watch(
  () => props.checklist,
  (newChecklist, oldChecklist) => {
    // On initial mount, always sync from props if available
    if (isInitialMount.value) {
      isInitialMount.value = false
      if (newChecklist) {
        localChecklist.value = normalizeChecklist(JSON.parse(JSON.stringify(newChecklist)))
      }
      return
    }

    const shouldSync = shouldSyncFromProps(newChecklist)

    if (shouldSync) {
      if (newChecklist) {
        localChecklist.value = normalizeChecklist(JSON.parse(JSON.stringify(newChecklist)))
        // If we synced successfully and mutation was in progress, reset the flag
        // This means the refetch completed and we have the updated data
        if (mutationInProgress.value) {
          mutationInProgress.value = false
        }
      } else if (!localChecklist.value) {
        // Only clear if we don't have local state (shouldn't happen after initial mount)
        localChecklist.value = null
      }
    } else {
      // If props came back with the same data as local (refetch completed), reset mutation flag
      if (mutationInProgress.value && newChecklist && localChecklist.value) {
        if (newChecklist.id === localChecklist.value.id) {
          const propsItemCount = newChecklist.items?.length || 0
          const localItemCount = localChecklist.value.items?.length || 0
          // If props has same or more items, refetch completed successfully
          if (propsItemCount >= localItemCount) {
            mutationInProgress.value = false
          }
        }
      }
    }
  },
  { immediate: true, deep: true }
)

// State
const showCreateForm = ref(false)
const creating = ref(false)
const saving = ref(false)
const error = ref('')
const createForm = ref({ itemTitle: '' })
const showAddItemForm = ref(false)
const newItemForm = ref({ title: '' })
const editingItems = ref({})
const isDragging = ref(false)
const sortableInstance = ref(null)
const isMobileScreen = ref(false)
const checklistCopyFeedbackVisible = ref(false)
let checklistCopyFeedbackTimer = null

// Refs
const createItemInput = ref(null)
const newItemInput = ref(null)
const itemInput = ref(null)
const itemsList = ref(null)

function updateMobileScreenState() {
  if (typeof window === 'undefined') return
  isMobileScreen.value = window.innerWidth <= 767
}

function buildChecklistCopyText() {
  const checklist = localChecklist.value
  if (!checklist) return ''

  const lines = []
  ;(checklist.items || []).forEach(item => {
    const marker = item.completed ? '[x]' : '[ ]'
    lines.push(`${marker} ${item.title}`)
  })
  return lines.join('\n')
}

function setChecklistCopyFeedbackVisible() {
  checklistCopyFeedbackVisible.value = true
  if (checklistCopyFeedbackTimer) {
    clearTimeout(checklistCopyFeedbackTimer)
  }
  checklistCopyFeedbackTimer = setTimeout(() => {
    checklistCopyFeedbackVisible.value = false
    checklistCopyFeedbackTimer = null
  }, 1500)
}

async function copyChecklistToClipboard() {
  const textToCopy = buildChecklistCopyText()
  if (!textToCopy) return

  await copyTextToClipboard(textToCopy)
}

async function copyChecklistItemToClipboard(item) {
  const textToCopy = String(item?.title || '').trim()
  if (!textToCopy) return

  await copyTextToClipboard(textToCopy)
}

async function copyTextToClipboard(textToCopy) {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(textToCopy)
    } else {
      const textarea = document.createElement('textarea')
      textarea.value = textToCopy
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'absolute'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    setChecklistCopyFeedbackVisible()
  } catch {
    // Silently ignore copy errors (e.g., clipboard permission denied).
  }
}

// Computed
const canCreateChecklist = computed(() => {
  return createForm.value.itemTitle.trim().length > 0
})

const canAddItem = computed(() => {
  return newItemForm.value.title.trim().length > 0
})

const checklistItems = computed(() => {
  return Array.isArray(localChecklist.value?.items) ? localChecklist.value.items : []
})

const sortedItems = computed(() => {
  // Return references to actual items, not copies, so v-model works correctly
  return checklistItems.value.slice().sort((a, b) => a.position - b.position)
})

const allCompleted = computed(() => {
  return sortedItems.value.length > 0 && sortedItems.value.every(item => item.completed)
})

const allIncomplete = computed(() => {
  return sortedItems.value.length > 0 && sortedItems.value.every(item => !item.completed)
})

const completedItemsCount = computed(() => {
  return checklistItems.value.filter(item => item.completed).length
})

const completionPercentage = computed(() => {
  if (!localChecklist.value) return 0
  return localChecklist.value.completionPercentage || 0
})

// GraphQL Mutations
const CREATE_CHECKLIST = gql`
  mutation CreateChecklist($taskId: ID!, $title: String) {
    createChecklist(taskId: $taskId, title: $title) {
      checklist {
        id
        taskId
        title
        completed
        completionPercentage
        items {
          id
          checklistId
          title
          completed
          position
        }
      }
      errors
    }
  }
`

const DESTROY_CHECKLIST = gql`
  mutation DestroyChecklist($id: ID!) {
    destroyChecklist(id: $id) {
      success
      errors
    }
  }
`

const CREATE_CHECKLIST_ITEM = gql`
  mutation CreateChecklistItem($checklistId: ID!, $title: String!, $position: Int) {
    createChecklistItem(checklistId: $checklistId, title: $title, position: $position) {
      item {
        id
        checklistId
        title
        completed
        position
      }
      errors
    }
  }
`

const UPDATE_CHECKLIST_ITEM = gql`
  mutation UpdateChecklistItem($id: ID!, $title: String, $position: Int) {
    updateChecklistItem(id: $id, title: $title, position: $position) {
      item {
        id
        title
        position
      }
      errors
    }
  }
`

const DESTROY_CHECKLIST_ITEM = gql`
  mutation DestroyChecklistItem($id: ID!) {
    destroyChecklistItem(id: $id) {
      success
      errors
    }
  }
`

const TOGGLE_CHECKLIST_ITEM = gql`
  mutation ToggleChecklistItem($id: ID!) {
    toggleChecklistItem(id: $id) {
      item {
        id
        completed
      }
      checklist {
        id
        completed
        completionPercentage
      }
      errors
    }
  }
`

const BULK_TOGGLE_CHECKLIST_ITEMS = gql`
  mutation BulkToggleChecklistItems($checklistId: ID!, $completed: Boolean!) {
    bulkToggleChecklistItems(checklistId: $checklistId, completed: $completed) {
      checklist {
        id
        completed
        completionPercentage
        items {
          id
          completed
        }
      }
      updatedCount
      errors
    }
  }
`

const REORDER_CHECKLIST_ITEMS = gql`
  mutation ReorderChecklistItems($checklistId: ID!, $itemPositions: [ItemPositionInput!]!) {
    reorderChecklistItems(checklistId: $checklistId, itemPositions: $itemPositions) {
      checklist {
        id
        items {
          id
          position
        }
      }
      errors
    }
  }
`

const { mutate: createChecklistMutation } = useMutation(CREATE_CHECKLIST)
const { mutate: destroyChecklistMutation } = useMutation(DESTROY_CHECKLIST)
const { mutate: createItemMutation } = useMutation(CREATE_CHECKLIST_ITEM)
const { mutate: updateItemMutation } = useMutation(UPDATE_CHECKLIST_ITEM)
const { mutate: destroyItemMutation } = useMutation(DESTROY_CHECKLIST_ITEM)
const { mutate: toggleItemMutation } = useMutation(TOGGLE_CHECKLIST_ITEM)
const { mutate: bulkToggleMutation } = useMutation(BULK_TOGGLE_CHECKLIST_ITEMS)
const { mutate: reorderItemsMutation } = useMutation(REORDER_CHECKLIST_ITEMS)

// Methods
async function createChecklist() {
  if (!canCreateChecklist.value || creating.value) return

  creating.value = true
  error.value = ''
  const firstItemTitle = createForm.value.itemTitle.trim()

  try {
    const { data } = await createChecklistMutation({
      taskId: props.taskId,
      title: null,
    })

    if (data?.createChecklist?.errors?.length) {
      error.value = data.createChecklist.errors.join(', ')
      return
    }

    const createdChecklist = JSON.parse(JSON.stringify(data.createChecklist.checklist))
    const itemResult = await createItemMutation({
      checklistId: createdChecklist.id,
      title: firstItemTitle,
      position: 1,
    })

    if (itemResult.data?.createChecklistItem?.errors?.length) {
      error.value = itemResult.data.createChecklistItem.errors.join(', ')
      return
    }

    const createdItem = itemResult.data?.createChecklistItem?.item
    createdChecklist.items = createdItem ? [createdItem] : []
    createdChecklist.completed = false
    createdChecklist.completionPercentage = 0

    // Update local state immediately
    mutationInProgress.value = true
    localChecklist.value = createdChecklist
    emit('checklist-created', createdChecklist)
    showCreateForm.value = false
    createForm.value.itemTitle = ''
    // Flag will be reset when props come back with matching data
  } catch (e) {
    error.value = e.message || s__('Checklist|Failed to create checklist')
  } finally {
    creating.value = false
  }
}

function cancelCreate() {
  showCreateForm.value = false
  createForm.value.itemTitle = ''
  error.value = ''
}

async function confirmDelete() {
  if (!localChecklist.value) return
  if (!confirm(s__('Checklist|Delete this checklist?'))) return

  saving.value = true
  error.value = ''

  try {
    const { data } = await destroyChecklistMutation({
      id: localChecklist.value.id,
    })

    if (data?.destroyChecklist?.errors?.length) {
      error.value = data.destroyChecklist.errors.join(', ')
      return
    }

    // Update local state immediately
    localChecklist.value = null
    emit('checklist-deleted')
  } catch (e) {
    error.value = e.message || s__('Checklist|Failed to delete checklist')
  } finally {
    saving.value = false
  }
}

function startEditItem(item) {
  editingItems.value[item.id] = { title: item.title }
  nextTick(() => {
    const input = itemInput.value
    if (input && typeof input.focus === 'function') {
      input.focus()
    }
  })
}

function cancelEditItem(item) {
  delete editingItems.value[item.id]
}

async function saveItem(item) {
  if (!editingItems.value[item.id] || !editingItems.value[item.id].title.trim()) return

  saving.value = true
  error.value = ''

  try {
    const { data } = await updateItemMutation({
      id: item.id,
      title: editingItems.value[item.id].title.trim(),
    })

    if (data?.updateChecklistItem?.errors?.length) {
      error.value = data.updateChecklistItem.errors.join(', ')
      return
    }

    // Update local state immediately
    if (localChecklist.value?.items) {
      const itemIndex = localChecklist.value.items.findIndex(i => i.id === item.id)
      if (itemIndex !== -1) {
        localChecklist.value.items[itemIndex].title = data.updateChecklistItem.item.title
        if (data.updateChecklistItem.item.position !== undefined) {
          localChecklist.value.items[itemIndex].position = data.updateChecklistItem.item.position
        }
      }
    }
    emit('checklist-updated', localChecklist.value)
    delete editingItems.value[item.id]
  } catch (e) {
    error.value = e.message || s__('Checklist|Failed to update item')
  } finally {
    saving.value = false
  }
}

async function confirmDeleteItem(item) {
  if (!confirm(s__('Checklist|Delete this item?'))) return

  saving.value = true
  error.value = ''

  try {
    const { data } = await destroyItemMutation({
      id: item.id,
    })

    if (data?.destroyChecklistItem?.errors?.length) {
      error.value = data.destroyChecklistItem.errors.join(', ')
      return
    }

    // Update local state immediately
    if (localChecklist.value?.items) {
      localChecklist.value.items = localChecklist.value.items.filter(i => i.id !== item.id)
      // Recalculate completion percentage
      const total = localChecklist.value.items.length
      const completed = localChecklist.value.items.filter(i => i.completed).length
      localChecklist.value.completionPercentage =
        total > 0 ? Math.round((completed / total) * 100) : 0
      localChecklist.value.completed = completed === total && total > 0
    }
    emit('checklist-updated', localChecklist.value)
  } catch (e) {
    error.value = e.message || s__('Checklist|Failed to delete item')
  } finally {
    saving.value = false
  }
}

async function toggleItem(item) {
  if (saving.value) return

  // Update local state immediately for instant UI feedback
  mutationInProgress.value = true
  const previousCompleted = item.completed
  const itemIndex = localChecklist.value?.items?.findIndex(i => i.id === item.id)
  if (itemIndex !== -1 && localChecklist.value?.items) {
    localChecklist.value.items[itemIndex].completed = !previousCompleted
    // Recalculate completion percentage immediately
    const total = localChecklist.value.items.length
    const completed = localChecklist.value.items.filter(i => i.completed).length
    localChecklist.value.completionPercentage =
      total > 0 ? Math.round((completed / total) * 100) : 0
    localChecklist.value.completed = completed === total && total > 0
  }

  saving.value = true
  error.value = ''

  try {
    const { data, errors: graphqlErrors } = await toggleItemMutation({
      id: item.id,
    })

    if (graphqlErrors?.length) {
      error.value = graphqlErrors.map(e => e.message).join(', ')
      // Revert the checkbox state
      if (itemIndex !== -1 && localChecklist.value?.items) {
        localChecklist.value.items[itemIndex].completed = previousCompleted
        const total = localChecklist.value.items.length
        const completed = localChecklist.value.items.filter(i => i.completed).length
        localChecklist.value.completionPercentage =
          total > 0 ? Math.round((completed / total) * 100) : 0
        localChecklist.value.completed = completed === total && total > 0
      }
      return
    }

    if (data?.toggleChecklistItem?.errors?.length) {
      error.value = data.toggleChecklistItem.errors.join(', ')
      // Revert the checkbox state
      if (itemIndex !== -1 && localChecklist.value?.items) {
        localChecklist.value.items[itemIndex].completed = previousCompleted
        const total = localChecklist.value.items.length
        const completed = localChecklist.value.items.filter(i => i.completed).length
        localChecklist.value.completionPercentage =
          total > 0 ? Math.round((completed / total) * 100) : 0
        localChecklist.value.completed = completed === total && total > 0
      }
      return
    }

    if (!data?.toggleChecklistItem?.item) {
      error.value = s__('Checklist|Failed to toggle item - no response')
      // Revert the checkbox state
      if (itemIndex !== -1 && localChecklist.value?.items) {
        localChecklist.value.items[itemIndex].completed = previousCompleted
        const total = localChecklist.value.items.length
        const completed = localChecklist.value.items.filter(i => i.completed).length
        localChecklist.value.completionPercentage =
          total > 0 ? Math.round((completed / total) * 100) : 0
        localChecklist.value.completed = completed === total && total > 0
      }
      return
    }

    // Sync with server response to ensure consistency
    if (data?.toggleChecklistItem?.item && localChecklist.value?.items && itemIndex !== -1) {
      localChecklist.value.items[itemIndex].completed = data.toggleChecklistItem.item.completed
    }
    if (data?.toggleChecklistItem?.checklist && localChecklist.value) {
      localChecklist.value.completed = data.toggleChecklistItem.checklist.completed
      localChecklist.value.completionPercentage =
        data.toggleChecklistItem.checklist.completionPercentage
    }
    emit('checklist-updated', data.toggleChecklistItem.checklist)
    // Flag will be reset when props come back with matching data
  } catch (e) {
    mutationInProgress.value = false
    error.value = e.message || s__('Checklist|Failed to toggle item')
    // Revert the checkbox state
    if (itemIndex !== -1 && localChecklist.value?.items) {
      localChecklist.value.items[itemIndex].completed = previousCompleted
      const total = localChecklist.value.items.length
      const completed = localChecklist.value.items.filter(i => i.completed).length
      localChecklist.value.completionPercentage =
        total > 0 ? Math.round((completed / total) * 100) : 0
      localChecklist.value.completed = completed === total && total > 0
    }
  } finally {
    saving.value = false
  }
}

async function addItem(options = {}) {
  if (!canAddItem.value || saving.value || !localChecklist.value) return
  const { keepFormOpen = false } = options

  saving.value = true
  error.value = ''

  try {
    // Calculate position (add at end)
    const maxPosition =
      sortedItems.value.length > 0 ? Math.max(...sortedItems.value.map(i => i.position)) : -1

    const { data } = await createItemMutation({
      checklistId: localChecklist.value.id,
      title: newItemForm.value.title.trim(),
      position: maxPosition + 1,
    })

    if (data?.createChecklistItem?.errors?.length) {
      error.value = data.createChecklistItem.errors.join(', ')
      return
    }

    // Update local state immediately
    mutationInProgress.value = true
    if (localChecklist.value) {
      if (!localChecklist.value.items) {
        localChecklist.value.items = []
      }
      const newItem = JSON.parse(JSON.stringify(data.createChecklistItem.item))
      localChecklist.value.items.push(newItem)
      // Recalculate completion percentage
      const total = localChecklist.value.items.length
      const completed = localChecklist.value.items.filter(i => i.completed).length
      localChecklist.value.completionPercentage =
        total > 0 ? Math.round((completed / total) * 100) : 0
      localChecklist.value.completed = completed === total && total > 0
    }
    emit('checklist-updated', localChecklist.value)
    newItemForm.value.title = ''
    if (!keepFormOpen) {
      showAddItemForm.value = false
    }
    nextTick(() => {
      initializeSortable()
      if (keepFormOpen && newItemInput.value?.focus) {
        newItemInput.value.focus()
      }
      // Flag will be reset when props come back with matching data
    })
  } catch (e) {
    error.value = e.message || s__('Checklist|Failed to add item')
  } finally {
    saving.value = false
  }
}

function handleAddItemTab(event) {
  if (!newItemForm.value.title.trim() || saving.value) {
    return
  }

  event.preventDefault()
  addItem({ keepFormOpen: true })
}

function handleAddItemEnter(event) {
  if (!newItemForm.value.title.trim() || saving.value) {
    return
  }

  event.preventDefault()
  addItem({ keepFormOpen: true })
}

function cancelAddItem() {
  showAddItemForm.value = false
  newItemForm.value.title = ''
}

async function bulkToggle(completed) {
  if (saving.value || !localChecklist.value) return

  saving.value = true
  error.value = ''

  try {
    const { data } = await bulkToggleMutation({
      checklistId: localChecklist.value.id,
      completed: completed,
    })

    if (data?.bulkToggleChecklistItems?.errors?.length) {
      error.value = data.bulkToggleChecklistItems.errors.join(', ')
      return
    }

    // Update local state immediately
    if (data?.bulkToggleChecklistItems?.checklist && localChecklist.value) {
      // Update item completion states
      if (localChecklist.value.items && data.bulkToggleChecklistItems.checklist.items) {
        const itemMap = new Map(
          data.bulkToggleChecklistItems.checklist.items.map(i => [i.id, i.completed])
        )
        localChecklist.value.items.forEach(item => {
          if (itemMap.has(item.id)) {
            item.completed = itemMap.get(item.id)
          }
        })
      }
      // Update checklist completion state
      localChecklist.value.completed = data.bulkToggleChecklistItems.checklist.completed
      localChecklist.value.completionPercentage =
        data.bulkToggleChecklistItems.checklist.completionPercentage
    }
    emit('checklist-updated', data.bulkToggleChecklistItems.checklist)
  } catch (e) {
    error.value = e.message || s__('Checklist|Failed to toggle items')
  } finally {
    saving.value = false
  }
}

// Initialize Sortable for drag-and-drop
function initializeSortable() {
  if (!itemsList.value || !props.canUpdate || isMobileScreen.value) {
    if (sortableInstance.value) {
      sortableInstance.value.destroy()
      sortableInstance.value = null
    }
    return
  }

  // Destroy existing instance
  if (sortableInstance.value) {
    sortableInstance.value.destroy()
    sortableInstance.value = null
  }

  // Create new Sortable instance
  sortableInstance.value = Sortable.create(itemsList.value, {
    handle: '.item-drag-handle',
    animation: 150,
    ghostClass: 'sortable-ghost',
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    onStart: () => {
      isDragging.value = true
    },
    onEnd: async event => {
      isDragging.value = false

      // Don't do anything if item wasn't actually moved
      if (event.oldIndex === event.newIndex) return

      // Build item positions array
      const itemPositions = []
      const items = Array.from(itemsList.value.children)
      items.forEach((el, index) => {
        const itemId = el.getAttribute('data-item-id')
        if (itemId) {
          itemPositions.push({
            itemId: itemId,
            position: index,
          })
        }
      })

      // Call mutation to reorder
      saving.value = true
      error.value = ''

      try {
        if (!localChecklist.value) return
        const { data } = await reorderItemsMutation({
          checklistId: localChecklist.value.id,
          itemPositions: itemPositions,
        })

        if (data?.reorderChecklistItems?.errors?.length) {
          error.value = data.reorderChecklistItems.errors.join(', ')
          // Revert the order
          sortableInstance.value.sort(
            sortedItems.value.map(i => i.id.toString()),
            { animation: 150 }
          )
          return
        }

        // Update local state immediately
        if (data?.reorderChecklistItems?.checklist?.items && localChecklist.value?.items) {
          const positionMap = new Map(
            data.reorderChecklistItems.checklist.items.map(i => [i.id, i.position])
          )
          localChecklist.value.items.forEach(item => {
            if (positionMap.has(item.id)) {
              item.position = positionMap.get(item.id)
            }
          })
          // Re-sort items array
          localChecklist.value.items.sort((a, b) => a.position - b.position)
        }
        emit('checklist-updated', localChecklist.value)
      } catch (e) {
        error.value = e.message || s__('Checklist|Failed to reorder items')
        // Revert the order
        sortableInstance.value.sort(
          sortedItems.value.map(i => i.id.toString()),
          { animation: 150 }
        )
      } finally {
        saving.value = false
      }
    },
  })
}

// Watch for checklist changes to reinitialize sortable
watch(
  () => localChecklist.value?.items?.length,
  () => {
    nextTick(() => {
      if (localChecklist.value && props.canUpdate) {
        initializeSortable()
      }
    })
  }
)

// Initialize on mount
onMounted(() => {
  updateMobileScreenState()
  window.addEventListener('resize', updateMobileScreenState)
  if (localChecklist.value && props.canUpdate) {
    nextTick(() => {
      initializeSortable()
    })
  }
})

// Cleanup on unmount
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateMobileScreenState)
  if (sortableInstance.value) {
    sortableInstance.value.destroy()
    sortableInstance.value = null
  }
  if (checklistCopyFeedbackTimer) {
    clearTimeout(checklistCopyFeedbackTimer)
    checklistCopyFeedbackTimer = null
  }
})

watch(isMobileScreen, () => {
  nextTick(() => {
    initializeSortable()
  })
})

// Focus create form input when shown
watch(showCreateForm, async shown => {
  if (shown) {
    await nextTick()
    createItemInput.value?.focus()
  }
})

// Focus add item input when shown
watch(showAddItemForm, async shown => {
  if (shown) {
    await nextTick()
    newItemInput.value?.focus()
  }
})

function openCreateForm() {
  if (!props.canUpdate || localChecklist.value) return
  showCreateForm.value = true
  createForm.value.itemTitle = ''
  error.value = ''
}

defineExpose({
  openCreateForm,
})
</script>

<style scoped>
.checklist-container {
  margin-bottom: 16px;
}

.checklist-empty {
  padding: 12px;
  text-align: left;
}

.checklist-empty-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.checklist {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px;
  background: #fafafa;
}

.checklist-header {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
}

.checklist-title-section {
  flex: 1;
}

.checklist-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.checklist-title {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
}

.checklist-title-row .checklist-title {
  margin: 0;
}

.checklist-progress {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(135deg, #2aabee 0%, #229ed9 100%);
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
}

.checklist-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.checklist-items {
  margin-bottom: 12px;
}

.checklist-items-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.checklist-items-list.is-dragging {
  cursor: grabbing;
}

.checklist-item {
  display: flex;
  gap: 8px;
  padding: 8px;
  margin-bottom: 4px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s;
}

.checklist-item:hover {
  background: #f5f5f5;
}

.checklist-item.completed {
  opacity: 0.7;
}

.checklist-item.completed .item-text {
  text-decoration: line-through;
  color: #999;
}

.item-content {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  min-width: 28px;
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #8a8a8a;
  cursor: grab;
  touch-action: none;
}

.item-drag-handle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.item-drag-handle:active {
  cursor: grabbing;
}

.item-drag-handle:hover {
  background: #f2f2f2;
}

.drag-handle-dots {
  width: 10px;
  height: 16px;
  background-image: radial-gradient(circle, #9ca3af 1.4px, transparent 1.5px);
  background-size: 5px 5px;
}

.item-checkbox {
  cursor: pointer;
  flex-shrink: 0;
}

.item-label {
  flex: 1;
  min-width: 0;
  cursor: pointer;
  margin: 0;
}

.item-text {
  display: block;
  user-select: none;
  overflow-wrap: anywhere;
}

.item-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.item-edit-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.item-edit-input {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.item-edit-actions {
  display: flex;
  gap: 8px;
}

.checklist-empty-items {
  padding: 12px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.checklist-footer {
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

.checklist-footer-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.add-item-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.item-input {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.add-item-actions {
  display: flex;
  gap: 8px;
}

.create-checklist-form {
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
}

.form-row {
  margin-bottom: 12px;
}

.form-row label {
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
  font-size: 14px;
}

.form-row input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-actions {
  display: flex;
  gap: 8px;
}

.checklist-warning {
  margin-top: 12px;
  padding: 12px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  color: #856404;
  font-size: 14px;
}

/* Button styles inherit from forms.css */

.btn-icon-small {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 2px 4px;
  opacity: 0.7;
  transition: opacity 0.2s;
  border-radius: 4px;
}

.btn-icon-small:hover {
  background: #ebedf1;
  opacity: 1;
}

.checklist-actions .btn-icon-small:focus-visible,
.item-actions .btn-icon-small:focus-visible {
  background: #ebedf1;
  outline: 2px solid #d1d5db;
  outline-offset: 1px;
  opacity: 1;
}

.checklist-actions .btn-icon-small:active,
.item-actions .btn-icon-small:active {
  background: #e5e7eb;
  opacity: 1;
}

.muted {
  color: #999;
}

.error {
  color: #dc3545;
  font-size: 14px;
  margin-top: 8px;
}

@media (max-width: 767px) {
  .item-edit-input,
  .item-input,
  .form-row input {
    font-size: 16px;
  }
}

/* Sortable styles */
.sortable-ghost {
  opacity: 0.4;
}

.sortable-chosen {
  cursor: grabbing;
}

.sortable-drag {
  opacity: 0.8;
}
</style>
